import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CanActivateFn, Router } from '@angular/router';

import { SkToastService } from '@mercadoni/stark-ui';
import { filter, map, take } from 'rxjs';

import { AuthService } from '../services/auth/auth.service';
import { AuthzService } from '../services/authz/authz.service';
import { translateNow } from '../services/i18n/translate-now';

/**
 * Blocks navigation to admin routes for anyone whose mapped role isn't 'admin'.
 *
 * Waits for AuthService.isLoading() to settle before deciding: on a fresh navigation right
 * after login, Auth0 may not have resolved the user yet, so checking userRole() synchronously
 * here could still see it fall back to 'support' and wrongly deny an actual admin.
 */
export const adminGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const authz = inject(AuthzService);
  const router = inject(Router);
  const toast = inject(SkToastService);

  return toObservable(authService.isLoading).pipe(
    filter(isLoading => !isLoading),
    take(1),
    map(() => {
      const ctx = { role: authService.userRole(), clientId: authService.clientId() };
      if (authz.isAdmin(ctx)) return true;
      toast.error(translateNow('errors.adminAccessDenied'));
      return router.createUrlTree(['/']);
    }),
  );
};
